import React, { useState, useEffect } from 'react';
import { 
  Settings, Save, Calculator, Percent, Calendar, DollarSign,
  Building, Clock, Globe, CreditCard, Bell, Shield, Info,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Separator } from '../../ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { db } from '../../../lib/db';
import { formatCurrency } from '../../../lib/payrollCalculations';
import { toast } from 'sonner';

export default function PayrollSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [taxSettings, setTaxSettings] = useState([]);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [newTaxBracket, setNewTaxBracket] = useState({
    minIncome: 0,
    maxIncome: 0,
    rate: 0
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [payrollSettings, taxData] = await Promise.all([
        db.payrollSettings.get(1),
        db.taxSettings.toArray()
      ]);
      
      if (payrollSettings) {
        setSettings(payrollSettings);
      } else {
        // Default settings - create and save to database
        const defaultSettings = {
          id: 1,
          companyName: 'Company Inc.',
          payrollFrequency: 'monthly',
          payDay: 25,
          currency: 'USD',
          currencySymbol: '$',
          fiscalYearStart: 1,
          overtimeMultiplier: 1.5,
          weekendMultiplier: 2.0,
          standardWorkHours: 8,
          standardWorkDays: 5,
          enableAutoPayroll: false,
          enableEmailNotifications: true,
          enableSlackNotifications: false,
          requireApproval: true,
          approvalLevels: 1,
          pfContributionEmployee: 12,
          pfContributionEmployer: 12,
          enablePF: true,
          enableESI: false,
          esiThreshold: 21000,
          esiEmployeeRate: 0.75,
          esiEmployerRate: 3.25,
          enableProfessionalTax: true,
          ptMaxAmount: 200,
          gracePeriodDays: 3,
          lateDeductionPercentage: 0.5
        };
        // Save default settings to database
        await db.payrollSettings.put(defaultSettings);
        setSettings(defaultSettings);
      }
      
      setTaxSettings(taxData.sort((a, b) => a.minIncome - b.minIncome));
    } catch (error) {
      console.error('Error loading settings:', error);
      // Even on error, set default settings so page is usable
      setSettings({
        id: 1,
        companyName: 'Company Inc.',
        payrollFrequency: 'monthly',
        payDay: 25,
        currency: 'USD',
        currencySymbol: '$',
        fiscalYearStart: 1,
        overtimeMultiplier: 1.5,
        weekendMultiplier: 2.0,
        standardWorkHours: 8,
        standardWorkDays: 5,
        enableAutoPayroll: false,
        enableEmailNotifications: true,
        enableSlackNotifications: false,
        requireApproval: true,
        approvalLevels: 1,
        pfContributionEmployee: 12,
        pfContributionEmployer: 12,
        enablePF: true,
        enableESI: false,
        esiThreshold: 21000,
        esiEmployeeRate: 0.75,
        esiEmployerRate: 3.25,
        enableProfessionalTax: true,
        ptMaxAmount: 200,
        gracePeriodDays: 3,
        lateDeductionPercentage: 0.5
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await db.payrollSettings.put(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleAddTaxBracket = async () => {
    try {
      const id = await db.taxSettings.add({
        ...newTaxBracket,
        year: new Date().getFullYear(),
        type: 'income'
      });
      setShowTaxModal(false);
      setNewTaxBracket({ minIncome: 0, maxIncome: 0, rate: 0 });
      loadSettings();
      toast.success('Tax bracket added');
    } catch (error) {
      toast.error('Failed to add tax bracket');
    }
  };

  const handleDeleteTaxBracket = async (id) => {
    try {
      await db.taxSettings.delete(id);
      loadSettings();
      toast.success('Tax bracket removed');
    } catch (error) {
      toast.error('Failed to remove tax bracket');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Safety check - if settings is still null after loading, show error
  if (!settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500">Failed to load payroll settings.</p>
          <Button 
            onClick={loadSettings}
            className="mt-4"
            variant="outline"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Settings</h1>
          <p className="text-gray-500 mt-1">Configure payroll policies and preferences</p>
        </div>
        <Button 
          onClick={handleSaveSettings}
          disabled={saving}
          className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-gray-100/80">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="salary">Salary & Deductions</TabsTrigger>
          <TabsTrigger value="tax">Tax Settings</TabsTrigger>
          <TabsTrigger value="overtime">Overtime & Leave</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-teal-600" />
                Company Settings
              </CardTitle>
              <CardDescription>Basic payroll configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={settings.companyName}
                    onChange={(e) => updateSetting('companyName', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <Select value={settings.currency} onValueChange={(value) => updateSetting('currency', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                      <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                      <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="payrollFrequency">Payroll Frequency</Label>
                  <Select value={settings.payrollFrequency} onValueChange={(value) => updateSetting('payrollFrequency', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="payDay">Pay Day (Day of Month)</Label>
                  <Input
                    id="payDay"
                    type="number"
                    min="1"
                    max="31"
                    value={settings.payDay}
                    onChange={(e) => updateSetting('payDay', parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="fiscalYearStart">Fiscal Year Start Month</Label>
                  <Select value={settings.fiscalYearStart?.toString()} onValueChange={(value) => updateSetting('fiscalYearStart', parseInt(value))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">January</SelectItem>
                      <SelectItem value="4">April</SelectItem>
                      <SelectItem value="7">July</SelectItem>
                      <SelectItem value="10">October</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Require Approval for Payroll</Label>
                  <p className="text-sm text-gray-500">Payroll must be approved before processing</p>
                </div>
                <Switch
                  checked={settings.requireApproval}
                  onCheckedChange={(checked) => updateSetting('requireApproval', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Auto-Generate Payroll</Label>
                  <p className="text-sm text-gray-500">Automatically generate payroll on pay day</p>
                </div>
                <Switch
                  checked={settings.enableAutoPayroll}
                  onCheckedChange={(checked) => updateSetting('enableAutoPayroll', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Salary & Deductions */}
        <TabsContent value="salary" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-teal-600" />
                401(k) Retirement Plan
              </CardTitle>
              <CardDescription>Configure employee 401(k) retirement contributions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Enable 401(k)</Label>
                  <p className="text-sm text-gray-500">Enable 401(k) contributions for eligible employees</p>
                </div>
                <Switch
                  checked={settings.enablePF}
                  onCheckedChange={(checked) => updateSetting('enablePF', checked)}
                />
              </div>

              {settings.enablePF && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="pfEmployee">Employee Contribution (%)</Label>
                    <Input
                      id="pfEmployee"
                      type="number"
                      step="0.1"
                      value={settings.pfContributionEmployee}
                      onChange={(e) => updateSetting('pfContributionEmployee', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pfEmployer">Employer Match (%)</Label>
                    <Input
                      id="pfEmployer"
                      type="number"
                      step="0.1"
                      value={settings.pfContributionEmployer}
                      onChange={(e) => updateSetting('pfContributionEmployer', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                State Disability Insurance (SDI)
              </CardTitle>
              <CardDescription>Configure SDI contributions for eligible states (CA, NY, NJ, etc.)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Enable SDI</Label>
                  <p className="text-sm text-gray-500">Apply SDI for employees in applicable states</p>
                </div>
                <Switch
                  checked={settings.enableESI}
                  onCheckedChange={(checked) => updateSetting('enableESI', checked)}
                />
              </div>

              {settings.enableESI && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="esiThreshold">Wage Base Limit</Label>
                    <Input
                      id="esiThreshold"
                      type="number"
                      value={settings.esiThreshold}
                      onChange={(e) => updateSetting('esiThreshold', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="esiEmployee">Employee Rate (%)</Label>
                    <Input
                      id="esiEmployee"
                      type="number"
                      step="0.01"
                      value={settings.esiEmployeeRate}
                      onChange={(e) => updateSetting('esiEmployeeRate', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="esiEmployer">Employer Rate (%)</Label>
                    <Input
                      id="esiEmployer"
                      type="number"
                      step="0.01"
                      value={settings.esiEmployerRate}
                      onChange={(e) => updateSetting('esiEmployerRate', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-teal-600" />
                Local/City Tax
              </CardTitle>
              <CardDescription>Configure local or city tax deductions (if applicable)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Enable Local Tax</Label>
                  <p className="text-sm text-gray-500">Deduct local/city tax where applicable</p>
                </div>
                <Switch
                  checked={settings.enableProfessionalTax}
                  onCheckedChange={(checked) => updateSetting('enableProfessionalTax', checked)}
                />
              </div>

              {settings.enableProfessionalTax && (
                <div>
                  <Label htmlFor="ptMax">Maximum Monthly Amount</Label>
                  <Input
                    id="ptMax"
                    type="number"
                    value={settings.ptMaxAmount}
                    onChange={(e) => updateSetting('ptMaxAmount', parseFloat(e.target.value))}
                    className="mt-1 max-w-xs"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Settings */}
        <TabsContent value="tax" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-teal-600" />
                  Income Tax Brackets
                </CardTitle>
                <CardDescription>Configure progressive tax rates</CardDescription>
              </div>
              <Dialog open={showTaxModal} onOpenChange={setShowTaxModal}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white">
                    Add Tax Bracket
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Tax Bracket</DialogTitle>
                    <DialogDescription>Define a new income tax bracket</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="minIncome">Minimum Income</Label>
                      <Input
                        id="minIncome"
                        type="number"
                        value={newTaxBracket.minIncome}
                        onChange={(e) => setNewTaxBracket(prev => ({ ...prev, minIncome: parseFloat(e.target.value) }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="maxIncome">Maximum Income (0 for no limit)</Label>
                      <Input
                        id="maxIncome"
                        type="number"
                        value={newTaxBracket.maxIncome}
                        onChange={(e) => setNewTaxBracket(prev => ({ ...prev, maxIncome: parseFloat(e.target.value) }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="taxRate">Tax Rate (%)</Label>
                      <Input
                        id="taxRate"
                        type="number"
                        step="0.1"
                        value={newTaxBracket.rate}
                        onChange={(e) => setNewTaxBracket(prev => ({ ...prev, rate: parseFloat(e.target.value) }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowTaxModal(false)}>Cancel</Button>
                    <Button onClick={handleAddTaxBracket}>Add Bracket</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Income Range</TableHead>
                    <TableHead className="text-center">Tax Rate</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxSettings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                        No tax brackets configured
                      </TableCell>
                    </TableRow>
                  ) : (
                    taxSettings.map(bracket => (
                      <TableRow key={bracket.id}>
                        <TableCell>
                          {formatCurrency(bracket.minIncome)} - {bracket.maxIncome === 0 ? 'No Limit' : formatCurrency(bracket.maxIncome)}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-semibold text-teal-600">{bracket.rate}%</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600"
                            onClick={() => handleDeleteTaxBracket(bracket.id)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-blue-50">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Tax Calculation Method</p>
                  <p className="text-sm text-blue-700 mt-1">
                    The system uses progressive tax calculation. Each bracket applies only to income within that range.
                    For example, if the first bracket is 0-10000 at 10% and second is 10000-50000 at 20%, 
                    an income of 30000 would be taxed as: (10000 × 10%) + (20000 × 20%) = $5,000.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overtime & Leave */}
        <TabsContent value="overtime" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-600" />
                Working Hours & Overtime
              </CardTitle>
              <CardDescription>Configure standard working hours and overtime rates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="standardHours">Standard Work Hours per Day</Label>
                  <Input
                    id="standardHours"
                    type="number"
                    value={settings.standardWorkHours}
                    onChange={(e) => updateSetting('standardWorkHours', parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="standardDays">Standard Work Days per Week</Label>
                  <Input
                    id="standardDays"
                    type="number"
                    value={settings.standardWorkDays}
                    onChange={(e) => updateSetting('standardWorkDays', parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="overtimeMultiplier">Overtime Rate Multiplier</Label>
                  <Input
                    id="overtimeMultiplier"
                    type="number"
                    step="0.1"
                    value={settings.overtimeMultiplier}
                    onChange={(e) => updateSetting('overtimeMultiplier', parseFloat(e.target.value))}
                    className="mt-1"
                  />
                  <p className="text-sm text-gray-500 mt-1">Applied to hourly rate for overtime hours</p>
                </div>
                <div>
                  <Label htmlFor="weekendMultiplier">Weekend Rate Multiplier</Label>
                  <Input
                    id="weekendMultiplier"
                    type="number"
                    step="0.1"
                    value={settings.weekendMultiplier}
                    onChange={(e) => updateSetting('weekendMultiplier', parseFloat(e.target.value))}
                    className="mt-1"
                  />
                  <p className="text-sm text-gray-500 mt-1">Applied to hourly rate for weekend work</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-teal-600" />
                Attendance & Leave
              </CardTitle>
              <CardDescription>Configure attendance policies and leave deductions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="gracePeriod">Late Arrival Grace Period (minutes)</Label>
                  <Input
                    id="gracePeriod"
                    type="number"
                    value={settings.gracePeriodDays}
                    onChange={(e) => updateSetting('gracePeriodDays', parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lateDeduction">Late Deduction (% of daily salary)</Label>
                  <Input
                    id="lateDeduction"
                    type="number"
                    step="0.1"
                    value={settings.lateDeductionPercentage}
                    onChange={(e) => updateSetting('lateDeductionPercentage', parseFloat(e.target.value))}
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-teal-600" />
                Notification Settings
              </CardTitle>
              <CardDescription>Configure how employees receive payroll notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Email Notifications</Label>
                  <p className="text-sm text-gray-500">Send payslip notifications via email</p>
                </div>
                <Switch
                  checked={settings.enableEmailNotifications}
                  onCheckedChange={(checked) => updateSetting('enableEmailNotifications', checked)}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Slack Notifications</Label>
                  <p className="text-sm text-gray-500">Send payroll alerts to Slack channels</p>
                </div>
                <Switch
                  checked={settings.enableSlackNotifications}
                  onCheckedChange={(checked) => updateSetting('enableSlackNotifications', checked)}
                />
              </div>

              <Separator />

              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <p className="font-medium text-gray-900">Notification Events</p>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Payslip generated and available for download
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Bonus/incentive credited to account
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Reimbursement approved/rejected
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Salary revision notification
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
